(() => {
  'use strict';

  /*
   * ============================================================
   * JJ Meta Signup Tracking
   * ============================================================
   *
   * Responsibilities:
   *
   * 1. Detect Facebook / Instagram ad traffic
   * 2. Preserve attribution during the browser session
   * 3. Provide a provider-independent signup tracker:
   *
   *      window.JJMetaTracking.trackSignup(email)
   *
   * 4. Integrate with Bluecore when Bluecore is present
   *
   * 5. Send Meta CompleteRegistration only for Meta-attributed
   *    visitors.
   *
   * This allows the signup provider to change:
   *
   *      Bluecore
   *      Attentive
   *      Klaviyo
   *      Shopify form
   *      Custom form
   *
   * without changing the Meta tracking logic.
   */

  const META_ATTRIBUTION_KEY = 'jj_meta_ad_visitor';

  const PATCHED_EMAILS_KEY =
    'jj_bluecore_meta_patched';

  const META_SIGNUP_TRACKED_KEY =
    'jj_meta_signup_tracked';


  /*
   * ============================================================
   * 1. META TRAFFIC DETECTION
   * ============================================================
   */

  function isMetaAdVisit() {
    const params = new URLSearchParams(
      window.location.search
    );

    const source =
      (params.get('utm_source') || '').toLowerCase();

    const medium =
      (params.get('utm_medium') || '').toLowerCase();

    const metaSources = [
      'facebook',
      'instagram',
      'fb',
      'ig',
      'meta'
    ];

    const paidMediums = [
      'paid_social',
      'paidsocial',
      'paid-social',
      'cpc'
    ];

    const hasFbclid = params.has('fbclid');

    const hasMetaUtm =
      metaSources.includes(source) &&
      paidMediums.includes(medium);

    return hasFbclid || hasMetaUtm;
  }


  /*
   * ============================================================
   * 2. CAPTURE META ATTRIBUTION
   * ============================================================
   */

  function captureMetaAttribution() {
    if (!isMetaAdVisit()) {
      return;
    }

    const params = new URLSearchParams(
      window.location.search
    );

    const attribution = {
      source:
        (params.get('utm_source') || 'meta')
          .toLowerCase(),

      medium:
        (params.get('utm_medium') || '')
          .toLowerCase(),

      campaign:
        params.get('utm_campaign') || '',

      fbclid:
        params.get('fbclid') || '',

      landingPage:
        window.location.href,

      capturedAt:
        Date.now()
    };

    try {
      sessionStorage.setItem(
        META_ATTRIBUTION_KEY,
        JSON.stringify(attribution)
      );

      console.log(
        '[JJ Meta Attribution] Meta visit captured',
        attribution
      );

    } catch (error) {
      console.warn(
        '[JJ Meta Attribution] Unable to store attribution',
        error
      );
    }
  }


  /*
   * ============================================================
   * 3. GET STORED META ATTRIBUTION
   * ============================================================
   */

  function getMetaAttribution() {
    try {
      const value =
        sessionStorage.getItem(
          META_ATTRIBUTION_KEY
        );

      return value
        ? JSON.parse(value)
        : null;

    } catch (error) {
      console.warn(
        '[JJ Meta Attribution] Unable to read attribution',
        error
      );

      return null;
    }
  }


  /*
   * ============================================================
   * 4. EMAIL VALIDATION
   * ============================================================
   */

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      email
    );
  }


  /*
   * ============================================================
   * 5. BLUECORE DUPLICATE CHECK
   * ============================================================
   */

  function hasAlreadyPatched(email) {
    try {
      const patched = JSON.parse(
        sessionStorage.getItem(
          PATCHED_EMAILS_KEY
        ) || '[]'
      );

      return patched.includes(
        email.toLowerCase()
      );

    } catch (error) {
      return false;
    }
  }


  function markAsPatched(email) {
    try {
      const patched = JSON.parse(
        sessionStorage.getItem(
          PATCHED_EMAILS_KEY
        ) || '[]'
      );

      const normalizedEmail =
        email.toLowerCase();

      if (!patched.includes(normalizedEmail)) {
        patched.push(normalizedEmail);
      }

      sessionStorage.setItem(
        PATCHED_EMAILS_KEY,
        JSON.stringify(patched)
      );

    } catch (error) {
      /*
       * Tracking failure must never
       * interfere with signup.
       */
    }
  }


  /*
   * ============================================================
   * 6. META SIGNUP DUPLICATE CHECK
   * ============================================================
   */

  function hasAlreadyTrackedMetaSignup(email) {
    try {
      const tracked = JSON.parse(
        sessionStorage.getItem(
          META_SIGNUP_TRACKED_KEY
        ) || '[]'
      );

      return tracked.includes(
        email.toLowerCase()
      );

    } catch (error) {
      return false;
    }
  }


  function markMetaSignupTracked(email) {
    try {
      const tracked = JSON.parse(
        sessionStorage.getItem(
          META_SIGNUP_TRACKED_KEY
        ) || '[]'
      );

      const normalizedEmail =
        email.toLowerCase();

      if (!tracked.includes(normalizedEmail)) {
        tracked.push(normalizedEmail);
      }

      sessionStorage.setItem(
        META_SIGNUP_TRACKED_KEY,
        JSON.stringify(tracked)
      );

    } catch (error) {
      /*
       * Tracking failure must never
       * interfere with signup.
       */
    }
  }


  /*
   * ============================================================
   * 7. BLUECORE CUSTOMER PATCH
   * ============================================================
   *
   * This is OPTIONAL.
   *
   * If Bluecore exists, we update the Bluecore customer.
   *
   * Meta visitor:
   *
   *      signup_source = meta
   *
   * Non-Meta visitor:
   *
   *      signup_source = direct
   *
   * This function does NOT send the Meta Pixel event.
   */

  function patchBluecoreCustomer(email) {
    console.log(
      '[JJ Bluecore] patchBluecoreCustomer()',
      email
    );

    if (!email) {
      return false;
    }

    email =
      email.trim().toLowerCase();

    if (!isValidEmail(email)) {
      console.log(
        '[JJ Bluecore] Invalid email'
      );

      return false;
    }

    if (
      !window.triggermail ||
      typeof window.triggermail.customer_patch !==
        'function'
    ) {
      console.log(
        '[JJ Bluecore] customer_patch unavailable'
      );

      return false;
    }

    if (hasAlreadyPatched(email)) {
      console.log(
        '[JJ Bluecore] Already patched',
        email
      );

      return true;
    }

    const attribution =
      getMetaAttribution();

    /*
     * Meta visitor = meta
     * Everything else = direct
     */
    const signupSource =
      attribution
        ? 'meta'
        : 'direct';

    console.log(
      '[JJ Bluecore] Calling customer_patch',
      {
        email,
        signup_source: signupSource,
        attribution
      }
    );

    try {
      window.triggermail.customer_patch({
        email: email,
        signup_source: signupSource
      });

      markAsPatched(email);

      console.log(
        '[JJ Bluecore] customer_patch finished',
        {
          email,
          signup_source: signupSource
        }
      );

      return true;

    } catch (error) {
      console.error(
        '[JJ Bluecore] customer_patch failed',
        error
      );

      return false;
    }
  }


  /*
   * ============================================================
   * 8. META COMPLETE REGISTRATION
   * ============================================================
   *
   * IMPORTANT:
   *
   * This function is provider-independent.
   *
   * Bluecore can call it.
   * Attentive can call it.
   * Another store can call it.
   * A custom Shopify form can call it.
   *
   * Example:
   *
   *      window.JJMetaTracking.trackSignup(email);
   *
   */

  function trackMetaSignup(email) {
    console.log(
      '[META] trackMetaSignup()',
      email
    );

    if (!email) {
      console.warn(
        '[META] No email supplied'
      );

      return false;
    }

    email =
      email.trim().toLowerCase();

    if (!isValidEmail(email)) {
      console.warn(
        '[META] Invalid email',
        email
      );

      return false;
    }

    /*
     * Only Meta-attributed visitors
     * should generate this Meta conversion.
     */
    const attribution =
      getMetaAttribution();

    console.log(
      '[META] attribution =',
      attribution
    );

    if (!attribution) {
      console.log(
        '[META] No Meta attribution - skipping CompleteRegistration'
      );

      return false;
    }


    /*
     * Prevent duplicate Meta conversions
     * during the same browser session.
     */
    if (
      hasAlreadyTrackedMetaSignup(email)
    ) {
      console.log(
        '[META] CompleteRegistration already tracked',
        email
      );

      return false;
    }


    /*
     * Verify Meta Pixel exists.
     */
    if (
      typeof window.fbq !== 'function'
    ) {
      console.warn(
        '[META] fbq is not available'
      );

      return false;
    }


    /*
     * Generate a unique event ID.
     *
     * This becomes useful if CAPI is added later
     * and we need browser/server deduplication.
     */
    const eventId =
      'jj_signup_' +
      Date.now() +
      '_' +
      Math.random()
        .toString(36)
        .slice(2);


    /*
     * Custom event data.
     */
    const data = {
      signup_source: 'Meta Source',

      signup_platform:
        attribution.source || 'meta',

      signup_medium:
        attribution.medium || '',

      signup_campaign:
        attribution.campaign || ''
    };


    console.log(
      '[META] Sending CompleteRegistration',
      {
        email,
        attribution,
        data,
        eventId
      }
    );


    try {

      window.fbq(
        'track',
        'CompleteRegistration',
        data,
        {
          eventID: eventId
        }
      );

      markMetaSignupTracked(email);

      console.log(
        '[META] CompleteRegistration sent',
        {
          email,
          eventId
        }
      );

      return true;

    } catch (error) {

      console.error(
        '[META] CompleteRegistration failed',
        error
      );

      return false;
    }
  }


  /*
   * ============================================================
   * 9. PUBLIC / PROVIDER-INDEPENDENT API
   * ============================================================
   *
   * This is the important part for future stores.
   *
   * Bluecore:
   *
   *      window.JJMetaTracking.trackSignup(email)
   *
   * Attentive:
   *
   *      window.JJMetaTracking.trackSignup(email)
   *
   * Custom form:
   *
   *      window.JJMetaTracking.trackSignup(email)
   *
   */

  window.JJMetaTracking = {
    trackSignup: trackMetaSignup,

    getAttribution: getMetaAttribution,

    isMetaAdVisit: isMetaAdVisit
  };


  /*
   * ============================================================
   * 10. BLUECORE FORM INTEGRATION
   * ============================================================
   *
   * This section only applies when the Bluecore
   * popup/form exists.
   *
   * If Bluecore is removed and Attentive is used,
   * this listener simply never fires.
   *
   * The Meta tracking API above remains available.
   */

  document.addEventListener(
    'click',
    event => {

      const button =
        event.target.closest(
          '#bluecoreEmailCaptureForm button'
        );

      if (!button) {
        return;
      }

      const form =
        button.closest('form');

      if (!form) {
        return;
      }

      const emailInput =
        form.querySelector(
          'input[type="email"], input[name*="email" i]'
        );

      if (!emailInput) {
        return;
      }

      const email =
        emailInput.value
          ? emailInput.value.trim()
          : '';

      if (
        !email ||
        !isValidEmail(email)
      ) {
        console.log(
          '[JJ Bluecore] Invalid email'
        );

        return;
      }

      console.log(
        '[JJ Bluecore] Bluecore button clicked',
        email
      );


      /*
       * Give Bluecore a moment to process
       * its own signup first.
       */
      window.setTimeout(() => {

        /*
         * Bluecore customer update.
         *
         * This is independent of Meta tracking.
         */
        patchBluecoreCustomer(email);


        /*
         * Meta conversion.
         *
         * This is provider-independent.
         */
        window.JJMetaTracking.trackSignup(
          email
        );

      }, 1000);

    },
    true
  );


  /*
   * ============================================================
   * 11. CAPTURE ATTRIBUTION ON PAGE LOAD
   * ============================================================
   */

  captureMetaAttribution();


  /*
   * ============================================================
   * 12. DEBUG INFORMATION
   * ============================================================
   */

  console.log(
    '[JJ Meta Tracking] Initialized',
    {
      metaAdVisit: isMetaAdVisit(),
      attribution: getMetaAttribution(),
      bluecoreAvailable:
        !!(
          window.triggermail &&
          typeof window.triggermail.customer_patch ===
            'function'
        ),
      metaPixelAvailable:
        typeof window.fbq === 'function'
    }
  );

})();